import { LitElement, TemplateResult, html } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
import { PluginOptionsByType, ActiveElement } from 'chart.js';
import Chart from 'chart.js/auto';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import * as ChartGeo from 'chartjs-chart-geo';
import {
  BubbleMapChartPoint,
  Country,
  LabelAlignOptions,
  LabelAnchorOptions,
  LabelWeights,
  MapChartEvents,
  MapConfig,
  ProjectionTypes,
} from '../types';
import {
  BubbleMapChart,
  BubbleMapController,
  ColorLogarithmicScale,
  ColorScale,
  GeoFeature,
  ProjectionScale,
  SizeLogarithmicScale,
  SizeScale,
} from 'chartjs-chart-geo';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';
import { worldCountries } from '../utils/world-countries';
import { latLongCountries } from '../utils/country-codes-lat-long';
import { WEB_COMPONENT_GZ_MAP_WITH_BUBBLES_CHART } from 'apps/webcomponents/src/webcomponents.constants';

/**
 * @summary This is a world map chart with bubbles. It is based on chartjs-chart-geo (a Chart.js module for charting maps with legends)
 * @tag gz-map-with-bubbles-chart
 * @fires bubbleClick - A custom event dispatched when a bubble/bubble with label is clicked
 * @fires bubbleLabelMouseEnter - A custom event dispatched when the mouse enters a bubble label
 * @fires bubbleLabelMouseLeave - A custom event dispatched when the mouse leaves a bubble label
 * See https://www.sgratzl.com/chartjs-chart-geo/ website
 */
@customElement(WEB_COMPONENT_GZ_MAP_WITH_BUBBLES_CHART)
export class GzMapWithBubblesChartWebComponent extends LitElement {
  @property({
    type: WidgetStateFrontEnd,
    attribute: true,
    reflect: true,
  })
  state: WidgetStateFrontEnd;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  bubblesData: BubbleMapChartPoint[];

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  private _mapConfig: MapConfig = {
    projectionType: ProjectionTypes.MERCADOR,
    hasLabels: true,
    hasTooltip: false,
    countriesBackgroundColor: getComputedStyle(document.body).getPropertyValue('--chart-105-solid'),
    countriesBorderColor: 'transparent',
    labelAnchor: LabelAnchorOptions.CENTER,
    labelAlign: LabelAlignOptions.CENTER,
    bubblesBackgroundColor: '#006DFF',
    labelFontColor: 'white',
    bubbleRadius: 1,
    hoverRadius: 1,
    labelOffset: 0,
    labelPadding: 8,
    labelBorderRadius: 20,
    labelFontSize: FontSizes.PARAGRAPH_12,
    labelFontWeight: LabelWeights.BOLD,
  };

  @query('#canvas')
  chartCanvas: HTMLCanvasElement;

  private chart;

  private readonly chartType = 'bubbleMap';
  private states;

  get mapConfig(): MapConfig {
    return this._mapConfig;
  }

  set mapConfig(value: Partial<MapConfig>) {
    this._mapConfig = {
      ...this._mapConfig,
      ...value,
    };
  }

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    return html` <canvas id="canvas"></canvas>`;
  }

  constructor() {
    super();

    Chart.register(
      BubbleMapController,
      BubbleMapChart,
      GeoFeature,
      ColorScale,
      ProjectionScale,
      SizeScale,
      ColorLogarithmicScale,
      SizeLogarithmicScale
    );

    if (this.mapConfig.hasLabels) {
      Chart.register(ChartDataLabels);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.chart) {
      this.chart.destroy();
    }
  }

  async updated(): Promise<void> {
    await this.updateComplete;

    if (!this.mapConfig.hasLabels) {
      Chart.unregister(ChartDataLabels);
    }

    if (!this.states) {
      const fetchedData = JSON.parse(JSON.stringify(worldCountries));
      this.states = ChartGeo.topojson.feature(fetchedData, fetchedData.objects.countries);
      if (this.states.hasOwnProperty('features')) {
        this.states = this.states.features.filter(f => !this.mapConfig.removedCountries?.includes(f.properties?.name));
        this.buildChart();
      } else {
        throw new Error(`Error fetching data`);
      }
    } else {
      this.buildChart();
    }
  }

  public resizeMap(): void {
    if (this.states) {
      this.buildChart();
    }
  }

  private buildChart(): void {
    if (!this.chartCanvas) {
      return;
    }

    const context = this.chartCanvas.getContext('2d');
    if (context) {
      // Destroy the old chart if it exists
      if (this.chart) {
        this.chart.destroy();
      }

      // default map aspect ratio
      let aspectRatio = 2;
      if (this.parentElement) {
        aspectRatio = this.parentElement.offsetWidth / this.parentElement.offsetHeight;
      }

      // Create a new chart
      this.chart = new BubbleMapChart(context, {
        data: {
          labels: this.buildLabelsArray(),
          datasets: [
            {
              outline: this.states,
              showOutline: true,
              backgroundColor: this.mapConfig.bubblesBackgroundColor,
              outlineBackgroundColor: this.mapConfig.countriesBackgroundColor,
              outlineBorderColor: this.mapConfig.countriesBorderColor ?? this.mapConfig.countriesBackgroundColor,
              data: this.buildPointsData(),
            },
          ],
        },
        options: {
          aspectRatio: aspectRatio,
          elements: {
            point: {
              radius: this.mapConfig.bubbleRadius,
              hoverRadius: this.mapConfig.hoverRadius ?? this.mapConfig.bubbleRadius,
            },
          },
          onHover: (event, elements, activeElements) => this.handleBubbleHover(elements),
          onClick: (event, elements) => this.handleBubbleClick(elements),
          plugins: {
            datalabels: this.getDataLabelConfigs(),
            legend: {
              display: false,
            },
            tooltip: {
              enabled: this.mapConfig.hasTooltip,
            },
          },
          scales: {
            xy: {
              projection: this.mapConfig.projectionType,
            },
            r: {
              display: false,
            },
          },
        },
      });
    }
  }

  private buildLabelsArray(): string[] {
    if (!this.bubblesData) {
      return [];
    }
    return this.bubblesData?.map(point => point.countryCode);
  }

  private buildPointsData(): Country[] {
    if (!this.bubblesData) {
      return [];
    }

    const countriesData = this.bubblesData?.map(point => {
      const { countryCode, value } = point;
      const country = latLongCountries.find(c => c.alpha2 === countryCode);
      if (!country) {
        console.error(`Country with code '${countryCode}' not found`);
        return;
      }

      return {
        country: country.country,
        alpha2: country.alpha2,
        latitude: country.latitude,
        longitude: country.longitude,
        value: value,
      } as Country;
    });

    return countriesData.filter((c): c is Country => c !== undefined);
  }

  private getDataLabelConfigs(): PluginOptionsByType<'bubble'>['datalabels'] {
    if (!this.mapConfig.hasLabels) {
      return undefined;
    }

    return {
      color: this.mapConfig.labelFontColor,
      anchor: this.mapConfig.labelAnchor,
      align: this.mapConfig.labelAlign,
      // offset only applicable if align is start or end
      offset: this.mapConfig.labelOffset,
      padding: this.mapConfig.labelPadding,
      listeners: {
        click: context => this.handleLabelEvent(context, MapChartEvents.BUBBLE_CLICK),
        enter: context => this.handleLabelEvent(context, MapChartEvents.BUBBLE_LABEL_MOUSE_ENTER),
        leave: context => this.handleLabelEvent(context, MapChartEvents.BUBBLE_LABEL_MOUSE_LEAVE),
      },
      borderRadius: this.mapConfig.labelBorderRadius,
      backgroundColor: this.mapConfig.labelBackgroundColor ?? this.mapConfig.bubblesBackgroundColor,
      font: () => {
        return {
          size: Number(this.mapConfig.labelFontSize),
          weight: this.mapConfig.labelFontWeight,
        };
      },
      formatter: (value, context) => this.labelFormatter(value, context),
    };
  }

  protected handleBubbleClick(elements: ActiveElement[]): void {
    if (this.mapConfig.hasLabels && this.isLabelAlignCenter()) {
      return;
    }

    const clickedElement = elements[0];
    if (clickedElement) {
      const label = this.chart.data.datasets[clickedElement.datasetIndex].data[clickedElement.index].country;
      const value = this.chart.data.datasets[clickedElement.datasetIndex].data[clickedElement.index].value;

      this.dispatchEvent(
        new CustomEvent(MapChartEvents.BUBBLE_CLICK, {
          detail: { label: label, value: value },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  protected handleBubbleHover(elements: ActiveElement[]): void {
    if (this.mapConfig.hasLabels && this.isLabelAlignCenter()) {
      return;
    }

    if (elements && Array.isArray(elements) && elements.length > 0) {
      this.chart.canvas.style.cursor = 'pointer';
    } else {
      // Reset cursor style to default when not hovering over an element
      this.chart.canvas.style.cursor = 'default';
    }
  }

  protected handleLabelEvent(context: Context, event: MapChartEvents): void {
    if (!this.isLabelAlignCenter() || !context?.chart?.canvas) {
      return;
    }

    const clickedBubble = this.getClickedBubbleFromContext(context);

    this.dispatchEvent(
      new CustomEvent(event, {
        detail: { label: clickedBubble?.country, value: clickedBubble?.value },
        bubbles: true,
        composed: true,
      })
    );

    if (event !== MapChartEvents.BUBBLE_CLICK) {
      const cursorStyle = event === MapChartEvents.BUBBLE_LABEL_MOUSE_ENTER ? 'pointer' : 'default';
      context.chart.canvas.style.cursor = cursorStyle;
    }
  }

  private isLabelAlignCenter(): boolean {
    return this.mapConfig.labelAlign === LabelAlignOptions.CENTER;
  }

  // defines label displayed on bubble
  protected labelFormatter(value: any, context: Context): string {
    // can use also this info: const dataset = context.dataset;
    return `${value.value}`;
  }

  private getClickedBubbleFromContext(context: Context): Country {
    const dataIndex = context.dataIndex;
    return context.dataset.data[dataIndex] as Country;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-map-with-bubbles-chart': GzMapWithBubblesChartWebComponent;
  }
}
